import { deepClone } from "@dextall/common-utilities";
import { BoundaryLoopsFactory } from "../boundaries/boundaryLoopsFactory";
import {
    CladdingCellExtrusionGeometryFactory,
    ExtrusionGeometry,
} from "../geometry/claddingCellExtrusionGeometryFactory";
import { PanelLouvreGrill } from "./panelLouvreGrill";
import { createLouvreSourceDefinition } from "./panelLouvreSourceDefinitionFactory";
import { PanelWindowSill } from "./panelWindowSill";
import type {
    CladdingCellStyle,
    ILouvreCurtainCircularCutout,
    ILouvreCurtainRectangularCutout,
    IPanelSourceLouvre,
} from "../../../responses/panelSource";
import type { IPanelLouvreCutouts } from "../../../responses/panelType";
import type { SystemSettings } from "../../../responses/systemSettings";
import type { PanelLouvre as PanelLouvreDefinition } from "@dextall/panel-hooks-editor-2d";

export class PanelLouvre {
    constructor(
        public readonly definition: PanelLouvreDefinition,
        private readonly louvreSill: PanelWindowSill,
        private readonly louvreGrill: PanelLouvreGrill,
        private readonly panelLouvreDefinition: IPanelSourceLouvre,
        private readonly systemSetting: SystemSettings,
    ) {
        this.rectangularCutouts = deepClone(panelLouvreDefinition.rectangularCutouts);
        this.circularCutouts = deepClone(panelLouvreDefinition.circularCutouts);
    }

    get openingWidth(): number {
        return this.panelLouvreDefinition.box.max.x - this.panelLouvreDefinition.box.min.x;
    }

    get openingHeight(): number {
        return this.panelLouvreDefinition.box.max.y - this.panelLouvreDefinition.box.min.y;
    }

    get frameWidth(): number {
        const { louvreFrameOffset } = this.systemSetting;
        return this.openingWidth - louvreFrameOffset.left - louvreFrameOffset.right;
    }

    get frameHeight(): number {
        const { louvreFrameOffset } = this.systemSetting;
        return this.openingHeight - louvreFrameOffset.top - louvreFrameOffset.bottom;
    }

    /** Louvre color. RAL code in `RAL 9001` format */
    get color(): string {
        return `RAL ${this.panelLouvreDefinition.color}`;
    }

    /** Louvre sill color. RAL code in `RAL 9001` format */
    get sillColor(): string {
        return `RAL ${this.panelLouvreDefinition.sillColor}`;
    }

    get sillDepth(): number {
        return this.panelLouvreDefinition.sillDepth;
    }

    readonly rectangularCutouts: ILouvreCurtainRectangularCutout[];

    readonly circularCutouts: ILouvreCurtainCircularCutout[];

    static create(
        panelOrigin: THREE.Vector3,
        panelLouvreDefinition: IPanelSourceLouvre,
        systemSetting: SystemSettings,
        transform: THREE.Matrix4,
        claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
        boundaryLoopsFactory: BoundaryLoopsFactory,
        panelStyle: CladdingCellStyle,
    ): PanelLouvre {
        const louvreSill = PanelWindowSill.createLouvreSill(
            panelLouvreDefinition,
            systemSetting,
            transform,
            boundaryLoopsFactory,
            claddingExtrusionFactory,
            panelStyle,
        );

        const louvreBoundaryLoop = boundaryLoopsFactory.createLoopFromBox(panelLouvreDefinition.box, transform);

        const louvreGrill = new PanelLouvreGrill(
            louvreBoundaryLoop,
            systemSetting.louvreFrameOffset,
            panelLouvreDefinition.color,
            boundaryLoopsFactory,
            claddingExtrusionFactory,
        );

        const definition = createLouvreSourceDefinition(panelOrigin, panelLouvreDefinition, systemSetting);

        return new PanelLouvre(definition, louvreSill, louvreGrill, panelLouvreDefinition, systemSetting);
    }

    createGeometry(): ExtrusionGeometry[] {
        return [this.louvreSill.createGeometry(), ...this.louvreGrill.createGeometry()];
    }

    setCutouts(cutouts: IPanelLouvreCutouts) {
        this.rectangularCutouts.splice(0, this.rectangularCutouts.length, ...cutouts.rectangularCutouts);
        this.circularCutouts.splice(0, this.circularCutouts.length, ...cutouts.circularCutouts);
    }
}
